import { useState } from 'react'

import { Box, Button } from '@factorialco/deprecated-design-system'
import { Form as FormObject } from 'factorial-form'
import { CancelCardDocument, CancelCardMutation } from 'generated/resources/graphql'
import { computed, makeObservable } from 'mobx'

import RadioGroup from 'modules/core/components/Inputs/RadioGroup'
import Modal from 'modules/core/components/Modal'
import { useLazyRef } from 'modules/core/hooks/useLazyRef'
import { useMutation } from 'modules/core/hooks/useMutation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { BankingCard } from 'modules/banking/lib/cards/types'

interface ModalProps {
  card: BankingCard
  onClose: () => void
}

class CreateCancelCardForm extends FormObject {
  constructor(values: Record<string, string>) {
    super(values, {
      cancellationReason: 'string',
    })
    makeObservable(this, {
      mandatoryFieldsEmpty: computed,
    })
  }

  get mandatoryFieldsEmpty() {
    const mandatoryFields: string[] = ['cancellationReason']

    return mandatoryFields.some((field) => {
      return !this.get(field).value
    })
  }
}

export const CancelModal = ({ card, onClose }: ModalProps) => {
  const form = useLazyRef(() => new CreateCancelCardForm({})).current

  const modalChildren = () => {
    return (
      <RadioGroup
        name="cancellationReason"
        field={form.get('cancellationReason')}
        options={[
          {
            value: 'stolen',
            label: i18n.t('expenses_v2.forms.fields.card_stolen.label'),
          },
          {
            value: 'lost',
            label: i18n.t('expenses_v2.forms.fields.card_lost.label'),
          },
          {
            value: 'other',
            label: i18n.t('expenses_v2.forms.fields.card_other.label'),
          },
        ]}
      />
    )
  }

  const [submitting, setSubmitting] = useState(false)

  const [cancelCard, { loading }] = useMutation(CancelCardDocument, {
    extractorFn: (data: CancelCardMutation) => data.bankingMutations.cancelBankingCard,
  })

  const handleSubmit = async () => {
    setSubmitting(true)

    const res = await cancelCard({
      variables: { id: card.id + '', cancellationReason: form.get('cancellationReason').value },
    })
    const result = handleResult(res, (errors) => {
      app.setFlash({
        type: 'wrong',
        message: errors['messages'] || i18n.t('errors.generic_error'),
      })
    })

    if (result.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('expenses_v2.cards.actions.modal.cancel.success'),
      })
    }

    setSubmitting(false)
    onClose()
  }

  return (
    <Modal
      title={i18n.t('expenses_v2.cards.actions.modal.cancel.title')}
      description={i18n.t('expenses_v2.cards.actions.modal.cancel.description')}
      illustration="alert"
      onSubmit={handleSubmit}
      onClose={onClose}
      footer={
        <Box flexDirection="row" gap="s16">
          <Button type="secondary" onClick={onClose}>
            {i18n.t('common.cancel')}
          </Button>
          <Button submit disabled={submitting || loading}>
            {i18n.t('expenses_v2.cards.actions.modal.cancel.text')}
          </Button>
        </Box>
      }
    >
      {modalChildren()}
    </Modal>
  )
}
